import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  createJobDetailPreloadQueue,
  orderPreloadTargets,
  parsePreloadJobIds,
  parsePreloadVersion,
  preloadTargetVersions,
  uniqueJobIds,
  PRELOAD_DATE_ATTR,
  PRELOAD_GROUP_ATTR,
  PRELOAD_IDS_ATTR,
  PRELOAD_LOOKAHEAD_PX,
  PRELOAD_PROXIMITY_COUNT,
  PRELOAD_PROXIMITY_PX,
  PRELOAD_PROXIMITY_THROTTLE_MS,
  PRELOAD_ROOT_ATTR,
  PRELOAD_SETTLE_MS,
  PRELOAD_TIME_ATTR,
  PRELOAD_UPDATED_ATTR,
  type JobDetailPreloadQueue,
  type PreloadTarget,
} from './job-detail-preload'

// Lets the tray and the month popover publish their own sources (expanded
// state, open popover) to the queue owned by the agenda page.
export const JobDetailPreloadContext =
  createContext<JobDetailPreloadQueue | null>(null)

export function useJobDetailPreloadQueue() {
  return useContext(JobDetailPreloadContext)
}

const TARGET_SELECTOR = `[${PRELOAD_IDS_ATTR}]`
const ROOT_SELECTOR = `[${PRELOAD_ROOT_ATTR}]`
// A change to any of these on an existing target changes what should be loaded
// or in which order.
const TARGET_ATTRIBUTES = [
  PRELOAD_IDS_ATTR,
  PRELOAD_UPDATED_ATTR,
  PRELOAD_DATE_ATTR,
  PRELOAD_TIME_ATTR,
]

function toIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

function readTarget(element: Element): PreloadTarget {
  return {
    jobIds: parsePreloadJobIds(element.getAttribute(PRELOAD_IDS_ATTR)),
    date: element.getAttribute(PRELOAD_DATE_ATTR),
    time: element.getAttribute(PRELOAD_TIME_ATTR),
    version: parsePreloadVersion(element.getAttribute(PRELOAD_UPDATED_ATTR)),
  }
}

function distanceToRect(x: number, y: number, rect: DOMRect): number {
  const dx = Math.max(rect.left - x, 0, x - rect.right)
  const dy = Math.max(rect.top - y, 0, y - rect.bottom)
  return Math.hypot(dx, dy)
}

interface UseAgendaJobPreloadOptions {
  view: 'day' | 'week' | 'month'
  // False until the calendar request has finished, so preloading never delays
  // the calendar itself.
  enabled: boolean
  // Changes with the date range, view and filters: what was queued for the
  // previous calendar is dropped at once.
  resetKey: string
  // True while a drawer covers the calendar; mouse moves over the overlay
  // should not reprioritise the cards underneath.
  proximityPaused: boolean
}

// Owns the agenda's job-detail preload queue and feeds it from the DOM:
// which targets are on screen (IntersectionObserver per scroll container) and
// which ones the mouse is heading for. See docs/AGENDA_JOB_PANEL_LOADING.md.
export function useAgendaJobPreload({
  view,
  enabled,
  resetKey,
  proximityPaused,
}: UseAgendaJobPreloadOptions) {
  const queryClient = useQueryClient()
  const queue = useMemo(
    () => createJobDetailPreloadQueue(queryClient),
    [queryClient]
  )
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    queue.setEnabled(enabled)
  }, [queue, enabled])

  useEffect(
    () => () => {
      queue.setEnabled(false)
      queue.clear()
    },
    [queue]
  )

  // Visibility: the queue only contains what is on screen. Re-running on
  // resetKey drops what was queued for the previous calendar and observes the
  // new one from scratch, so it is published even when it came from cache and
  // reused the same elements.
  useEffect(() => {
    const root = rootRef.current
    if (!root || typeof IntersectionObserver === 'undefined') return

    queue.setSource('calendar', [])

    const visible = new Set<Element>()
    const observerByTarget = new Map<Element, IntersectionObserver>()
    const observerByScrollRoot = new Map<Element, IntersectionObserver>()
    let settleTimer: number | undefined
    let syncFrame: number | undefined

    const publish = () => {
      const calendar: PreloadTarget[] = []
      const tray: PreloadTarget[] = []
      // Document order keeps tray cards left to right.
      root.querySelectorAll(TARGET_SELECTOR).forEach(element => {
        if (!visible.has(element)) return
        const group = element.getAttribute(PRELOAD_GROUP_ATTR)
        ;(group === 'tray' ? tray : calendar).push(readTarget(element))
      })
      const now = new Date()
      queue.setSource(
        'calendar',
        orderPreloadTargets(calendar, {
          view,
          todayIso: toIsoDate(now),
          nowMinutes: now.getHours() * 60 + now.getMinutes(),
        }),
        preloadTargetVersions(calendar)
      )
      queue.setSource('tray', uniqueJobIds(tray), preloadTargetVersions(tray))
    }

    const schedulePublish = () => {
      window.clearTimeout(settleTimer)
      settleTimer = window.setTimeout(publish, PRELOAD_SETTLE_MS)
    }

    const handleIntersections = (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (entry.isIntersecting) visible.add(entry.target)
        else visible.delete(entry.target)
      }
      schedulePublish()
    }

    const observerFor = (scrollRoot: Element) => {
      let observer = observerByScrollRoot.get(scrollRoot)
      if (!observer) {
        observer = new IntersectionObserver(handleIntersections, {
          root: scrollRoot,
          rootMargin: `${PRELOAD_LOOKAHEAD_PX}px`,
        })
        observerByScrollRoot.set(scrollRoot, observer)
      }
      return observer
    }

    // Cards mount and unmount as data loads, the view changes and technician
    // pages arrive; keep the observed set in step with the DOM.
    const sync = () => {
      syncFrame = undefined
      const current = new Set(root.querySelectorAll(TARGET_SELECTOR))
      let removed = false
      for (const [element, observer] of observerByTarget) {
        if (current.has(element)) continue
        observer.unobserve(element)
        observerByTarget.delete(element)
        removed = visible.delete(element) || removed
      }
      for (const element of current) {
        if (observerByTarget.has(element)) continue
        // Targets outside a scroll container (popover rows) take part in mouse
        // proximity only.
        const scrollRoot = element.closest(ROOT_SELECTOR)
        if (!scrollRoot) continue
        const observer = observerFor(scrollRoot)
        observer.observe(element)
        observerByTarget.set(element, observer)
      }
      for (const [scrollRoot, observer] of observerByScrollRoot) {
        if (scrollRoot.isConnected) continue
        observer.disconnect()
        observerByScrollRoot.delete(scrollRoot)
      }
      if (removed) schedulePublish()
    }

    const scheduleSync = () => {
      if (syncFrame === undefined) {
        syncFrame = window.requestAnimationFrame(sync)
      }
    }

    const mutationObserver = new MutationObserver(records => {
      // A visible target that keeps its element but changes its jobs or their
      // updated_at (a cell gaining a job, a rescheduled card) fires no
      // intersection change, so republish for it explicitly.
      if (records.some(record => record.type === 'attributes')) {
        schedulePublish()
      }
      scheduleSync()
    })
    mutationObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: TARGET_ATTRIBUTES,
    })
    sync()

    return () => {
      mutationObserver.disconnect()
      observerByScrollRoot.forEach(observer => observer.disconnect())
      window.clearTimeout(settleTimer)
      if (syncFrame !== undefined) window.cancelAnimationFrame(syncFrame)
    }
  }, [queue, view, resetKey])

  // Mouse proximity: users click almost immediately after reaching a card, so
  // the targets the pointer is approaching jump the queue before it arrives.
  useEffect(() => {
    queue.setSource('proximity', [])
    if (proximityPaused) return

    let lastRun = 0
    let lastKey = ''

    const handleMouseMove = (event: MouseEvent) => {
      // A pressed button means a drag or resize, not a move towards a click;
      // measuring every target then would only add work to that gesture.
      if (event.buttons !== 0) return
      const now = performance.now()
      if (now - lastRun < PRELOAD_PROXIMITY_THROTTLE_MS) return
      lastRun = now

      const near: { distance: number; target: PreloadTarget }[] = []
      // Document-wide: the month popover renders in a portal outside the page.
      document.querySelectorAll(TARGET_SELECTOR).forEach(element => {
        const rect = element.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return
        const distance = distanceToRect(event.clientX, event.clientY, rect)
        if (distance > PRELOAD_PROXIMITY_PX) return
        near.push({ distance, target: readTarget(element) })
      })
      near.sort((a, b) => a.distance - b.distance)
      const targets = near
        .slice(0, PRELOAD_PROXIMITY_COUNT)
        .map(item => item.target)
      const jobIds = uniqueJobIds(targets)
      const key = jobIds.join(',')
      if (key === lastKey) return
      lastKey = key
      queue.setSource('proximity', jobIds, preloadTargetVersions(targets))
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [queue, proximityPaused, resetKey])

  return { queue, rootRef }
}
